import { doc, getDoc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase/firebaseConfig';
import { AppInfoModel } from '../types/appInfo';

const SETTINGS_COLLECTION = 'settings';
const APP_INFO_DOC = 'app_info';

/**
 * Reads and writes the About Us content the mobile app shows.
 *
 * Unlike terms & conditions, this is not versioned: nobody has to accept an
 * About Us text, so a save overwrites the single document in place and is live
 * to the app immediately.
 */
export class AppInfoService {
  static async getAppInfo(): Promise<AppInfoModel> {
    try {
      const snapshot = await getDoc(doc(db, SETTINGS_COLLECTION, APP_INFO_DOC));

      if (!snapshot.exists()) {
        return { info: '', modifiedAt: null };
      }

      const data = snapshot.data();
      return {
        info: data.info ?? '',
        modifiedAt: (data.modifiedAt as Timestamp) ?? null,
      };
    } catch (error) {
      console.error('Error loading app info:', error);
      throw error;
    }
  }

  /**
   * Overwrites the stored content. Merged rather than replaced so any other
   * field on the document survives, and stamped with `modifiedAt` because the
   * app's model expects that field alongside `info`.
   */
  static async saveAppInfo(info: string): Promise<AppInfoModel> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, APP_INFO_DOC);

      await setDoc(
        docRef,
        { info, modifiedAt: serverTimestamp() },
        { merge: true }
      );

      // Re-read so the caller gets the server-resolved timestamp instead of the
      // null a pending serverTimestamp() returns locally.
      const saved = await getDoc(docRef);

      return {
        info,
        modifiedAt: (saved.data()?.modifiedAt as Timestamp) ?? null,
      };
    } catch (error) {
      console.error('Error saving app info:', error);
      throw error;
    }
  }
}
